/** One update offer per application window. Detection never reloads a page. */
export function createAppUpdate() {
  type Snapshot = { available: boolean; applying: boolean; failed: boolean };
  let snapshot: Snapshot = { available: false, applying: false, failed: false };
  let apply: (() => Promise<void>) | undefined;
  let approved = false;
  const listeners = new Set<() => void>();
  const set = (next: Snapshot) => {
    snapshot = next;
    listeners.forEach((listener) => listener());
  };
  return {
    getSnapshot: () => snapshot,
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    offer(action: () => Promise<void>) {
      apply = action;
      if (!snapshot.applying) set({ available: true, applying: false, failed: false });
    },
    /** Allows existing beforeunload guards to recognise explicit update consent. */
    isApproved: () => approved,
    async accept(beforeReload?: () => void) {
      if (!apply || snapshot.applying) return;
      set({ available: true, applying: true, failed: false });
      try {
        await apply();
        approved = true;
        beforeReload?.();
        window.location.reload();
      } catch {
        approved = false;
        set({ available: true, applying: false, failed: true });
      }
    },
  };
}

export type AppUpdate = ReturnType<typeof createAppUpdate>;

/** Version manifest is emitted by Vite only for a production build. */
export function watchAppVersion(update: AppUpdate, buildId: string) {
  let checking = false;
  const check = async () => {
    if (checking || document.visibilityState === 'hidden' || !navigator.onLine) return;
    checking = true;
    try {
      const response = await fetch(`/app-version.json?t=${Date.now()}`, {
        cache: 'no-store', signal: AbortSignal.timeout(10_000),
      });
      if (!response.ok) return;
      const version: unknown = await response.json();
      if (version && typeof version === 'object' && 'buildId' in version &&
          typeof version.buildId === 'string' && version.buildId && version.buildId !== buildId) {
        update.offer(async () => {
          // Verify the server is reachable before discarding an open editor.
          const current = await fetch(`/app-version.json?t=${Date.now()}`, {
            cache: 'no-store', signal: AbortSignal.timeout(10_000),
          });
          if (!current.ok) throw new Error('Update unavailable');
          const data = await current.json() as { buildId?: unknown };
          if (typeof data.buildId !== 'string' || !data.buildId) throw new Error('Invalid version');
        });
      }
    } catch { /* Offline, an old deployment or a transient error: keep working. */ }
    finally { checking = false; }
  };
  const onFocus = () => { void check(); };
  const timer = window.setInterval(onFocus, 60_000);
  window.addEventListener('focus', onFocus);
  window.addEventListener('online', onFocus);
  document.addEventListener('visibilitychange', onFocus);
  onFocus();
  return () => {
    clearInterval(timer);
    window.removeEventListener('focus', onFocus);
    window.removeEventListener('online', onFocus);
    document.removeEventListener('visibilitychange', onFocus);
  };
}

/** Wait for the waiting worker before reloading the consenting window only. */
export function activateWaitingWorker(registration: ServiceWorkerRegistration): Promise<void> {
  const worker = registration.waiting;
  // Another window may already have activated the same offered release.
  if (!worker && registration.active?.state === 'activated') return Promise.resolve();
  if (!worker) return Promise.reject(new Error('No update worker'));
  return new Promise<void>((resolve, reject) => {
    const finish = (error?: Error) => {
      clearTimeout(timer);
      worker.removeEventListener('statechange', changed);
      if (error) reject(error); else resolve();
    };
    const changed = () => {
      if (worker.state === 'activated') finish();
      else if (worker.state === 'redundant') finish(new Error('Update superseded'));
    };
    const timer = setTimeout(() => finish(new Error('Update timed out')), 30_000);
    worker.addEventListener('statechange', changed);
    worker.postMessage({ type: 'SKIP_WAITING' });
    changed();
  });
}
